export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'horizon.theme';
let fallbackPreference: ThemePreference = 'system';

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // Приватный режим или заблокированное хранилище — не повод падать.
    return fallbackPreference;
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(preference: ThemePreference): ResolvedTheme {
  fallbackPreference = preference;
  const resolved = resolveTheme(preference);
  document.documentElement.dataset['theme'] = resolved;
  const color = resolved === 'dark' ? '#101816' : '#f1f3f1';
  document.documentElement.style.backgroundColor = color;
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((meta) => {
    meta.content = color;
  });
  const statusBar = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-status-bar-style"]',
  );
  if (statusBar) statusBar.content = resolved === 'dark' ? 'black-translucent' : 'default';
  try {
    if (preference === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* хранилище недоступно — тема действует до перезагрузки */
  }
  window.dispatchEvent(new Event('horizon:theme-change'));
  return resolved;
}

export function subscribeThemePreference(onChange: () => void): () => void {
  const storage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) onChange();
  };
  window.addEventListener('horizon:theme-change', onChange);
  window.addEventListener('storage', storage);
  return () => {
    window.removeEventListener('horizon:theme-change', onChange);
    window.removeEventListener('storage', storage);
  };
}

export function watchSystemTheme(onChange: () => void): () => void {
  const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
  if (!media) return () => {};
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
